import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Map, Ship, ArrowRight, ShieldCheck, HeartHandshake, Award } from "lucide-react";
import { api, money } from "../../lib/api";
import { useSite } from "../../lib/auth";
import { FindTour } from "../../components/FindTour";

export const ItinCard = ({ it }) => (
  <Link to={`/itineraries/${it.slug}`} className="group block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 transition-shadow hover:shadow-xl" data-testid={`itinerary-card-${it.slug}`}>
    <div className="aspect-[16/10] overflow-hidden"><img src={it.hero_image} alt={it.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" /></div>
    <div className="p-5">
      <p className="eyebrow">{it.destination} · {it.duration_days} days</p>
      <h3 className="mt-2 text-2xl font-semibold leading-tight text-slate-900">{it.title}</h3>
      <div className="mt-4 flex items-center justify-between text-sm">
        <span className="chip bg-slate-100 capitalize text-slate-700">{it.tour_type === "individual" ? "Custom vacation" : it.tour_type === "group" ? "Group tour" : "Cruise"}</span>
        {it.starting_from && <span className="text-slate-600">from <b className="text-slate-900">{money(it.starting_from)}</b></span>}
      </div>
    </div>
  </Link>
);

export default function Home() {
  const [its, setIts] = useState([]);
  const { site } = useSite();
  useEffect(() => { api.get("/public/itineraries").then((r) => setIts(r.data)); }, []);
  return (
    <div>
      <section className="grain relative isolate flex min-h-[640px] items-end overflow-hidden" data-testid="home-hero">
        <img src="/images/italy.jpg" alt="Amalfi coast" className="absolute inset-0 -z-10 h-full w-full object-cover" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#0b1b33]/90 via-[#0b1b33]/40 to-transparent" />
        <div className="container-x pb-20 pt-32">
          <p className="eyebrow fade-up">Guided group journeys · custom vacations · cruises</p>
          <h1 className="fade-up mt-4 max-w-3xl text-4xl font-semibold leading-[1.05] text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: ".1s" }}>Travel the world together, beautifully arranged.</h1>
          <p className="fade-up mt-5 max-w-xl text-base text-slate-200 md:text-lg" style={{ animationDelay: ".2s" }}>{site.tagline || "Hosted tours for groups of 15 or more, and private journeys designed around you."}</p>
          <div className="fade-up mt-8 max-w-2xl" style={{ animationDelay: ".3s" }}>
            <FindTour big testid="hero-find-tour" />
            <p className="mt-3 text-sm text-slate-300">Have a tour code from your group leader? Enter it above to go straight to your tour.</p>
          </div>
        </div>
      </section>

      <section className="container-x -mt-10 grid gap-4 md:grid-cols-3">
        {[[Users, "Guided Group Tours", "For churches, alumni, clubs & friends of 15+", "/group-tours"], [Map, "Custom Vacations", "Private itineraries planned around you", "/custom-vacations"], [Ship, "Cruises", "Ocean & river voyages with expert support", "/cruises"]].map(([I, t, d, to], i) => (
          <Link key={to} to={to} className="card fade-up group flex items-start gap-4 p-6 hover:shadow-lg" style={{ animationDelay: `${0.1 * i}s` }} data-testid={`home-category-${to.slice(1)}`}>
            <div className="rounded-xl p-3 text-white" style={{ background: "var(--brand)" }}><I className="h-5 w-5" /></div>
            <div><h3 className="text-2xl font-semibold">{t}</h3><p className="text-sm text-slate-600">{d}</p></div>
            <ArrowRight className="ml-auto mt-2 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
          </Link>
        ))}
      </section>

      <section className="container-x mt-24">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="eyebrow">Signature itineraries</p><h2 className="mt-2 text-3xl font-semibold sm:text-4xl">Where will you go next?</h2></div>
          <Link to="/destinations" className="btn-outline" data-testid="home-all-destinations">All destinations <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{its.slice(0, 6).map((it) => <ItinCard key={it.id} it={it} />)}</div>
      </section>

      <section className="mt-24 bg-[#f3efe7]">
        <div className="container-x grid items-center gap-12 py-20 lg:grid-cols-2">
          <img src="/images/group.jpg" alt="A tour group at a historic gate" className="aspect-[4/3] w-full rounded-2xl object-cover shadow-xl" />
          <div>
            <p className="eyebrow">Why travel with us</p>
            <h2 className="mt-2 text-3xl font-semibold sm:text-4xl">Every detail handled, from first inquiry to the flight home.</h2>
            <div className="mt-8 space-y-6">
              {[[HeartHandshake, "A dedicated travel advocate", "One person who knows your group, your rooming and your questions."], [ShieldCheck, "Secure registration & payments", "Private tour pages, protected traveler documents and clear payment schedules."], [Award, "Expert local guides", "Hand-picked hotels, guides and partners in every destination."]].map(([I, t, d]) => (
                <div key={t} className="flex gap-4"><I className="mt-1 h-6 w-6 shrink-0" style={{ color: "var(--brand-accent)" }} /><div><h3 className="text-xl font-semibold">{t}</h3><p className="text-sm text-slate-600">{d}</p></div></div>
              ))}
            </div>
            <Link to="/quote" className="btn-primary mt-8" data-testid="home-quote-cta">Plan a group trip</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
